import { useState } from "react";
import { CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Column = ({ title, items, chosen, canPick, onPick, busy }) => (
  <div className="iv-glass rounded-2xl p-4">
    <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">{title}</p>
    <ul className="space-y-2">
      {items.map((n) => {
        const active = chosen === n.name;
        const slug = n.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        return (
          <li key={n.name} className={`flex items-start gap-3 rounded-xl border p-3 ${active ? "border-emerald-400/50 bg-emerald-500/5" : "border-white/[0.06]"}`} data-testid={`name-suggestion-${slug}`}>
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-white">
                {n.name}
                <span className="inline-flex items-center gap-0.5 font-mono text-[11px] text-amber-300"><Star className="h-3 w-3" aria-hidden="true" />{n.rating.toFixed(1)}/10</span>
              </p>
              <p className="text-xs text-slate-400">{n.reason}</p>
            </div>
            {canPick && (
              <button type="button" disabled={busy || active} onClick={() => onPick(n.name)} data-testid={`name-select-${slug}`}
                className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold ${active ? "bg-emerald-500/20 text-emerald-300" : "border border-white/10 text-slate-200 hover:border-cyan-400/50"}`}>
                {active ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" aria-hidden="true" />Chosen</span> : "Choose"}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

export const NameSuggestions = ({ report, isOwner, onChosen }) => {
  const names = report.evaluation.name_suggestions || [];
  const [chosen, setChosen] = useState(report.idea?.selected_brand_name || null);
  const [busy, setBusy] = useState(false);
  const pick = async (name) => {
    setBusy(true);
    try {
      await api.post(`/ideas/${report.idea.id}/brand-name`, { name });
      setChosen(name);
      onChosen?.(name);
      toast.success(`"${name}" saved as your brand name`);
    } catch {
      toast.error("Couldn't save the name. Please try again.");
    } finally { setBusy(false); }
  };
  if (!names.length) return null;
  return (
    <section aria-labelledby="names-title" data-testid="name-suggestions">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="names-title" className="text-lg font-bold text-white">Name Suggestions</h2>
        <p className="text-xs text-slate-400" data-testid="name-chosen-label">
          {chosen ? <>Your chosen name: <span className="font-bold text-emerald-300">{chosen}</span></> : isOwner ? "Pick your final name — the choice is yours." : "Reference case: run your own idea to choose a name."}
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Column title="5 Trendy / Happening" items={names.filter((n) => n.style === "trendy")} chosen={chosen} canPick={isOwner} onPick={pick} busy={busy} />
        <Column title="5 Professional" items={names.filter((n) => n.style === "professional")} chosen={chosen} canPick={isOwner} onPick={pick} busy={busy} />
      </div>
    </section>
  );
};
